import React, { useMemo, useState, useEffect, useRef } from 'react';
import { RemoveButton, SimpleRemoveButton } from './Buttons.jsx';
import MapViewer from './MapViewer.jsx';
import { VariableIcon } from '../constants/Constants.js';
import { tableFromIPC } from 'apache-arrow';
import { enrichedPath, statPath } from '../constants/Constants.js';
import RadarChart from './RadarChart.jsx';
import {AutoScalingKey, TaxonImage, TaxonomyTree, TaxonomyTreeItem} from './Helpers.jsx';


// functional wrappers
const HostTaxonomyTable = ({ data }) => <TaxonomyTree data={data} type="host" />;
const VirusTaxonomyTable = ({ data }) => <TaxonomyTree data={data} type="virus" />;
const NOOP = () => {};
const NON_NUMERIC_STATS = { isNumeric: false };


const SamplePanel = React.memo(function SamplePanel({ id, data, onRemove, viewMode }) {
  const { rowData, filename } = data;
  const [extraData, setExtraData] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [articleData, setArticleData] = useState(null);
  const [projectData, setProjectData] = useState(null);

  

   // Extract run information and metadata mapping
  const runInfo = useMemo(() => {
    if (!rowData) return { runs: [], libraries: [], assays: [], platforms: [], keys: {} };
    
    const getVals = (regex) => {
        const key = Object.keys(rowData).find(k => regex.test(k));
        return {
            key: key,
            vals: key ? String(rowData[key]).split('|').map(r => r.trim()).filter(Boolean) : []
        };
    };
    
    const r = getVals(/^run$|^run_id$/i);
    const l = getVals(/^librarysource$/i);
    const a = getVals(/^assay[\s_]?type$/i);
    const p = getVals(/^platform$/i);
    
    return {
        runs: r.vals,
        libraries: l.vals,
        assays: a.vals,
        platforms: p.vals,
        keys: { run: r.key, library: l.key, assay: a.key, platform: p.key }
    };
  }, [rowData]);

  const isRunMultiple = runInfo.runs.length > 1;

  const [gbifTaxonKey, setGbifTaxonKey] = useState(null);

  useEffect(() => {
    setGbifTaxonKey(null);
    const hostNameString = rowData?.['HOST_TAX_NAME'];
    if (!hostNameString) return;
    
    const hostName = String(hostNameString).split('|')[0].trim();
    const controller = new AbortController();
    
    const fetchGbifKey = async () => {
      try {
        const response = await fetch(`https://api.gbif.org/v1/species/match?name=${encodeURIComponent(hostName)}`, { signal: controller.signal });
        if (response.ok) {
          const data = await response.json();
          if (data.usageKey && data.matchType !== 'NONE') {
            setGbifTaxonKey(data.usageKey);
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') console.error("Failed to fetch GBIF key:", err);
      }
    };
    fetchGbifKey();
    return () => { controller.abort(); setGbifTaxonKey(null); };
  }, [rowData]);

  const gbifRasterUrl = useMemo(() => {
    if (!gbifTaxonKey) return undefined;
    return `https://api.gbif.org/v2/map/occurrence/density/{z}/{x}/{y}@2x.png?taxonKey=${gbifTaxonKey}&style=purpleYellow.point`;
  }, [gbifTaxonKey]);

  // Background fetch to infer BioProject from Run ID
  useEffect(() => {
    // CLEAR OLD DATA
    setProjectData(null);
    const runId = runInfo.runs.length > 0 ? runInfo.runs[0] : null;
    
    // Only proceed for NCBI/EBI/DDBJ run formats (SRR, ERR, DRR)
    if (!runId || !/^[SED]RR\d+/i.test(runId)) {
      setProjectData(null);
      return;
    }

    const controller = new AbortController();
    const fetchProject = async () => {
      try {
        // Step 1: Infer the Study Accession (BioProject) from the Run ID
        const runRes = await fetch(
          `https://www.ebi.ac.uk/ena/portal/api/search?result=read_run&query=run_accession="${runId}"&fields=study_accession&format=json`,
          { signal: controller.signal }
        );
        
        if (!runRes.ok) return;
        const runData = await runRes.json();
        if (!runData || runData.length === 0 || !runData[0].study_accession) return;
        
        const studyAccession = runData[0].study_accession;

        // Step 2: Fetch the Study/BioProject Title and Abstract
        const studyRes = await fetch(
          `https://www.ebi.ac.uk/ena/portal/api/search?result=study&query=study_accession="${studyAccession}"&fields=study_title,description&format=json`,
          { signal: controller.signal }
        );
        
        if (!studyRes.ok) return;
        const studyData = await studyRes.json();
        
        if (studyData && studyData.length > 0) {
          setProjectData({
            id: studyAccession,
            title: studyData[0].study_title,
            // map to description instead of study_abstract
            abstract: studyData[0].description 
          });
        } else {
          setProjectData({ id: studyAccession, title: null, abstract: null });
        }
      } catch (err) {
        if (err.name !== 'AbortError') console.error("Failed to fetch BioProject data:", err);
      }
    };

    fetchProject();
    // CLEAR ON CLEANUP
    return () => {
      controller.abort();
      setProjectData(null); 
    };
  }, [runInfo.runs]);

  // Background fetch for Publication / Article Information via PMID
  useEffect(() => {
    // CLEAR OLD DATA
    setArticleData(null);
    const pmidKey = Object.keys(rowData || {}).find(k => /^pmid$/i.test(k));
    const pmidString = pmidKey ? String(rowData[pmidKey]) : "";
    
    // In case there are multiple PMIDs separated by a pipe, take the first one
    const pmid = pmidString.split('|')[0].trim();
    
    // Only proceed if it's a valid numerical PMID
    if (!pmid || !/^\d+$/.test(pmid)) {
      setArticleData(null);
      return;
    }

    const controller = new AbortController();
    
    const fetchArticle = async () => {
      try {
        const response = await fetch(
          `https://www.ebi.ac.uk/europepmc/webservices/rest/search?query=ext_id:${pmid}&resultType=core&format=json`,
          { signal: controller.signal }
        );
        
        if (response.ok) {
          const data = await response.json();
          if (data.resultList && data.resultList.result && data.resultList.result.length > 0) {
            const article = data.resultList.result[0];
            
            // Clean HTML tags from the abstract if present
            const cleanAbstract = article.abstractText 
              ? article.abstractText.replace(/(<([^>]+)>)/gi, "") 
              : null;

            setArticleData({
              title: article.title,
              authors: article.authorString,
              journal: article.journalTitle,
              year: article.pubYear,
              abstract: cleanAbstract,
              doi: article.doi,
              pmid: pmid
            });
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error("Failed to fetch article data:", err);
        }
      }
    };

    fetchArticle();
    // CLEAR ON CLEANUP
    return () => {
      controller.abort();
      setArticleData(null);
    };
  }, [rowData]);

  // Background fetch for additional information
  useEffect(() => {
    // Create a controller to cancel this specific request if needed
    const controller = new AbortController();
    
    const fetchAdditionalInfo = async () => {
      const runIds = runInfo.runs;
      if (runIds.length === 0) {
        setSelectedRun(null);
        return;
      }
      setSelectedRun(runIds[0]);

      try {
        const results = {};
        let hasData = false;

        // Fetch data for all runs concurrently
        await Promise.all(runIds.map(async (runId) => {
          const url = new URL('/query', window.location.origin);
          url.searchParams.append("path", statPath); 
          url.searchParams.append("constraints", `run='${runId}'`);
          
          const response = await fetch(url, { signal: controller.signal }); 
          
          if (response.ok) {
            const table = await tableFromIPC(response.body);
            if (table.numRows > 0) {
              results[runId] = table.get(0).toJSON();
              hasData = true;
            }
          }
        }));

        // If at least one run returned data, update the states
        if (hasData) {
          setExtraData(results);
          // Auto-select the first run that has valid data
           setSelectedRun(runIds.find(id => results[id]) || runIds[0]);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error("Failed to fetch extended metadata", err);
        }
      }
    };

    // Clear old data immediately so it's ready for Garbage Collection
    setExtraData(null); 
    setSelectedRun(null);
    fetchAdditionalInfo();
    // Cleanup: This runs when the panel closes or when the sample changes
    return () => {
      controller.abort(); 
      setExtraData(null);  
      setSelectedRun(null);
    };
  }, [rowData, runInfo.runs]);

    const uniqueExtraEntries = useMemo(() => {
    // Return early if no data or no selection
    if (!extraData || !selectedRun || !extraData[selectedRun]) return [];
    
    // Process only the currently selected run's data
    const currentData = extraData[selectedRun];
    const initialKeys = new Set(Object.keys(rowData));

    //const keystokeep = [ 'Actinopterygii',
    //   'Amoebozoa', 'Annelida', 'Archaea', 'Arthropoda', 'Artiodactyla', 'Aves', 'Bacteria',
    //   'Carnivora', 'Cetartiodactyla', 'Chiroptera', 'Choanoflagellata',
    //   'Choanoflagellida', 'Chondrichthyes', 'Cnidaria', 'Cyclostomata',
    //   'Echinodermata', 'Embryophyta', 'Euglenozoa', 'Eukaryota',
    //   'Eumycetozoa', 'Fungi', 'Homo sapiens', 'Insecta', 'Mammalia',
    //   'Metamonada', 'Microsporidia', 'Mollusca', 'Mycetozoa', 'Nematoda',
    //   'Placozoa', 'Platyhelminthes', 'Porifera', 'Primates', 'Rodentia',
    //   'Sauropsida', 'Vertebrata', 'Viruses']; // 'Unclassified'

    const keystokeep = [ 'Virus', 'Homo sapiens', 'Bacteria','Archaea', 'Unclassified','total_spot_count']

    
    return Object.entries(currentData).filter(([key, val]) => {
        const isInternal = key === "_geojson_str" || key === "geom_id" || key === "total_spot_count";
        const isDuplicate = initialKeys.has(key);
        const isEmpty = val === null || val === '';
        return !isInternal && !isDuplicate && !isEmpty && keystokeep.some(k => key.toLowerCase().includes(k.toLowerCase()));
    });
  }, [extraData, selectedRun, rowData]);

  if (!rowData) return null;

  const entries = Object.entries(rowData);

  const sourceBadge = useMemo(() => {
    const sourceVal = rowData?.source ?? rowData?.Source ?? Object.entries(rowData || {}).find(([k]) => /^source$/i.test(k))?.[1];
    const s = String(sourceVal ?? '').trim();

    if (/^viral genbank$/i.test(s)) {
      return { text: 'Viral sequence', className: 'text-orange-700 w-fit' };
    }
    if (/^(ncbi biosample|gsa biosample)$/i.test(s)) {
      return { text: 'Biosample', className: 'text-green-700 w-fit' };
    }
    return null;
  }, [rowData]);

  // Detect coordinates for the maps
  const { lat, lon, precision } = useMemo(() => {
    const latKey = Object.keys(rowData).find(k => /latitude|^lat$/i.test(k));
    const lonKey = Object.keys(rowData).find(k => /longitude|^lon$/i.test(k));
    const precKey = Object.keys(rowData).find(k => /coordinates_precision/i.test(k));
    
    const latVal = parseFloat(rowData[latKey]);
    const lonVal = parseFloat(rowData[lonKey]);
    const precVal = precKey ? parseFloat(rowData[precKey]) : null;

    return {
      lat: isNaN(latVal) ? null : latVal,
      lon: isNaN(lonVal) ? null : lonVal,
      precision: isNaN(precVal) ? null : precVal
    };
  }, [rowData]);

  // Compute GeoJSON buffer based on coordinates precision
  const bufferGeoJSON = useMemo(() => {
    if (lat === null || lon === null || precision === null || precision <= 0.01) return null;
    
    const radius_m = (precision * 1000) / 2;
    const earthRadius_m = 6378137; // WGS84 approx equatorial radius
    const lat_rad = (lat * Math.PI) / 180;
    const lon_rad = (lon * Math.PI) / 180;
    const d_rad = radius_m / earthRadius_m;
    
    const coords = [];
    const num_points = 64;
    
    for (let i = 0; i <= num_points; i++) {
      const bearing_rad = (i * 360 / num_points * Math.PI) / 180;
      
      const target_lat = Math.asin(
        Math.sin(lat_rad) * Math.cos(d_rad) + 
        Math.cos(lat_rad) * Math.sin(d_rad) * Math.cos(bearing_rad)
      );
      const target_lon = lon_rad + Math.atan2(
        Math.sin(bearing_rad) * Math.sin(d_rad) * Math.cos(lat_rad),
        Math.cos(d_rad) - Math.sin(lat_rad) * Math.sin(target_lat)
      );
      
      // Normalize longitude to -180...180
      let lng = (target_lon * 180) / Math.PI;
      lng = ((lng + 540) % 360) - 180;
      
      coords.push([lng, (target_lat * 180) / Math.PI]);
    }
    
    return {
      type: 'Feature',
      properties: { status: 'buffer' },
      geometry: { type: 'Polygon', coordinates: [coords] }
    };
  }, [lat, lon, precision]);

  //console.log("Buffer GeoJSON:", bufferGeoJSON);

  // Calculate optimal zoom level to fit the buffer polygon
  const calculateOptimalZoom = (lat, precision) => {
    if (!precision || precision <= 0.01) return 10; // Fallback if no valid precision
    
    // The map container height is h-80 (320px). We want the buffer diameter 
    // to occupy about 60px to leave nice margins around it.
    const targetPixels = 60;
    const diameterMeters = precision * 1000;
    const metersPerPixel = diameterMeters / targetPixels;
    
    // Using standard Web Mercator math for 512px tiles (MapLibre/DeckGL default)
    // 40075016 (Earth circumference in meters) / 512 = 78271.516
    const latRad = (lat * Math.PI) / 180;
    const optimalZoom = Math.log2((78271.516 * Math.cos(latRad)) / metersPerPixel);
    
    // Clamp the zoom to prevent zooming in/out too extreme
    return Math.min(Math.max(optimalZoom, 2), 20);
  };

  // Initialize state using the dynamic calculation
  const [localViewState, setLocalViewState] = useState(() => {
    const initialZoom = (lat !== null && lon !== null) 
      ? calculateOptimalZoom(lat, precision) 
      : 0;

    return {
      longitude: lon || 0,
      latitude: lat || 0,
      zoom: initialZoom,
      pitch: 0,
      bearing: 0
    };
  });

  // Update viewState dynamically if lat/lon/precision changes
  useEffect(() => {
    if (lat !== null && lon !== null) {
      setLocalViewState({
        longitude: lon,
        latitude: lat,
        zoom: calculateOptimalZoom(lat, precision),
        pitch: 0,
        bearing: 0
      });
    }
  }, [lat, lon, precision]);

  const MiniMapData = useMemo(() => {
    if (lat === null || lon === null) return null;
    return {
      processedData: [{
        position: [lon, lat],
        count: 1,
        allSamples: [{ value: 'selected', index: 0 }],
        uniqueValues: new Set(['selected'])
      }],
      viewState: { longitude: 0, latitude: 0, zoom: -2, pitch: 0, bearing: 0 },
      colorConfig: { colorMap: new Map([['selected', [239, 68, 68, 255]]]) }
    };
  }, [lat, lon]);

    const MainMapData = useMemo(() => {
    if (lat === null || lon === null) return null;
    return {
      processedData: [{
        position: [lon, lat],
        count: 1,
        allSamples: [{ value: 'selected', index: 0 }],
        uniqueValues: new Set(['selected'])
      }],
      colorConfig: { colorMap: new Map([['selected', [239, 68, 68, 255]]]) }
    };
  }, [lat, lon]);
  
// Find specific keys for primary and secondary IDs
const keys = Object.keys(rowData);
//console.log("All keys in rowData:", keys);
const primaryKey = keys.find(k => /primary/i.test(k));
const secondaryKey = keys.find(k => /secondary/i.test(k));

// Determine titleKey: prioritize primary if it has a value, otherwise use secondary
let titleKey;
if (primaryKey && rowData[primaryKey] && String(rowData[primaryKey]).trim() !== '') {
  titleKey = primaryKey;
} else if (secondaryKey && rowData[secondaryKey] && String(rowData[secondaryKey]).trim() !== '') {
  titleKey = secondaryKey;
} else {
  // Original fallback logic if neither primary nor secondary have values
  titleKey = keys.find(k => /id$|accession|primary/i.test(k)) || keys[0];
}

const descriptionKey = keys.find(k => /HOST_TAX_NAME/i.test(k));

  // Helper for date formatting used in both manual lines and renderValue
  const formatAsDate = (val) => {
    if (val === null || val === undefined || val === '') return null;
    const numericVal = Number(val);
    const dateInput = !isNaN(numericVal) ? numericVal : val;
    const date = new Date(dateInput);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0].replace(/-/g, '/');
    }
    return val;
  };

  const renderValue = (key, val) => {
    // Remove brackets and single quotes from list-like values
    let displayValue = String(val ?? '').replace(/[\[\]']/g, "");
    const h = key.toLowerCase();

    if (/date/i.test(h) && val !== null && val !== undefined) {
      displayValue = formatAsDate(val);
    } else {
      const num = Number(displayValue);
      // Format numeric values to scientific notation with 4 decimals (excluding identifiers)
      if (!isNaN(num) && displayValue.trim() !== '' && !/id|tax|pmid|accession/i.test(h)) {
         displayValue = parseFloat(num.toExponential(5)).toString();
      }
    }

    const cellText = String(displayValue ?? '');
    if (!cellText || cellText.trim() === '') return cellText;

        const linkClass = "text-blue-600 hover:text-blue-800 hover:underline font-medium";
        let singleHref = null;
        const cleanText = cellText.trim();
        
        if (h === 'pmid') {
            singleHref = `https://pubmed.ncbi.nlm.nih.gov/${cleanText}/`;
        } else if (h === 'run') {
            // Links the SRR/ERR/DRR ID to the NCBI SRA database
            // if the value starts with CRA, link to NGDC instead
            if (cleanText.startsWith('CRA')) {
              singleHref = `https://ngdc.cncb.ac.cn/gsa/browse/${cleanText}`;
            } else  {
            singleHref = `https://www.ncbi.nlm.nih.gov/sra/?term=${cleanText}`;
            }
        } else if (h === 'primary_id') {
            if (/^SAM(D|EA|N)/i.test(cleanText)) singleHref = `https://www.ncbi.nlm.nih.gov/biosample/${cleanText}`;
            else if (/^SAMC/i.test(cleanText)) singleHref = `https://ngdc.cncb.ac.cn/biosample/browse/${cleanText}`;
        } else if (h === 'secondary_id') {
          if (cleanText.startsWith('IMGVR')) {
            singleHref = `https://www.meta-virome.org/uviginfo/${cleanText}`;
          } else {
            singleHref = `https://www.ncbi.nlm.nih.gov/nuccore/${cleanText}`;
          }
        } else if (h === 'virus_taxid') {
            singleHref = `https://www.ncbi.nlm.nih.gov/datasets/taxonomy/${cleanText}/`;
        } else if (h === 'mondo_id') {
        const urlId = cleanText.replace(':', '_');
        singleHref = `https://www.ebi.ac.uk/ols4/ontologies/mondo/classes/http%253A%252F%252Fpurl.obolibrary.org%252Fobo%252F${urlId}`;
        }

        if (singleHref) return <a href={singleHref} target="_blank" rel="noopener noreferrer" className={linkClass}>{cellText}</a>;

        if (['envo_id', 'uberon_id', 'host_taxid', 'environment_taxid'].includes(h)) {
            let href = h.includes('taxid') 
            ? `https://www.ncbi.nlm.nih.gov/datasets/taxonomy/${cleanText.replace(/\.0$/, '')}`
            : `http://purl.obolibrary.org/obo/${cleanText.replace(':', '_')}`;
            return <a href={href} target="_blank" rel="noopener noreferrer" className={linkClass}>{cellText}</a>;
        }

        if (cleanText.startsWith('http')) {
            return <a href={cleanText} target="_blank" rel="noopener noreferrer" className={linkClass}>{cellText}</a>;
        }

        return cellText;
  };

  // Logic for combined Collection Dates
  const startDate = formatAsDate(rowData['PARSED_COLLECTION_DATE_START']);
  const endDate = formatAsDate(rowData['PARSED_COLLECTION_DATE_END']);
  const collectionDatesDisplay = startDate && endDate ? `${startDate}-${endDate}` : (startDate || endDate);

  // Logic for combined Location
  const admin = rowData['FIRST_ADMIN_SUBDIVISION_NAME'];
  const country = rowData['COUNTRY_NAME'];
  let locationDisplay = null;
  if (lat !== null && lon !== null) {
    locationDisplay = `${lat}° ${'N'}, ${lon}° ${'E'}${admin ? `, ${admin}` : ''}${country ? `, ${country}` : ''}`;
  }

  // Keys that are manually handled so we can skip them in the loop
  let keysToSkip = ['PARSED_COLLECTION_DATE_START', 'PARSED_COLLECTION_DATE_END', 'COUNTRY_NAME', 'FIRST_ADMIN_SUBDIVISION_NAME', 'latitude', 'longitude',
    'HOST_TAXID', 'HOST_TAX_NAME','ENVIRONMENT_TAXID', 'ENVIRONMENT_TAX_NAME', "geometry", "HOST_PHYLUM", "HOST_CLASS", "HOST_ORDER", "HOST_FAMILY", "HOST_GENUS", "HOST_SPECIES", "HOST_KINGDOM", "HOST_DOMAIN",
    'HOST_SUPERKINGDOM', "VIRUS_PHYLUM", "VIRUS_CLASS", "VIRUS_ORDER", "VIRUS_FAMILY", "VIRUS_GENUS", "VIRUS_SPECIES", "VIRUS_KINGDOM", "VIRUS_DOMAIN", "VIRUS_SUPERKINGDOM",
    'VIRUS_TAXID', 'VIRUS_TAX_NAME', 'osm_id', 'geom_id', '_geojson_str'
  ];

  if (isRunMultiple) {
    const dynamicRunKeys = Object.keys(rowData).filter(k => /^(run|run_id|librarysource|assay[\s_]?type|platform)$/i.test(k));
    keysToSkip.push(...dynamicRunKeys);
  }

  // Map fallbacks in case MapTiler rate-limits or throws 403
  const mapFallbacks = useMemo(() => ['satellite', 'positron'], []);

  const runIndex = selectedRun ? runInfo.runs.indexOf(selectedRun) : -1;
  const hasExtraData = uniqueExtraEntries.length > 0;

  return (
    <div className="flex flex-col h-full bg-white border rounded-2xl overflow-hidden shadow-lg select-none">
        {viewMode === 'expert' ? (
            <div className="panel-header bg-gray-50 border-b p-2 flex justify-between items-center">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-bold truncate ml-2">Sample Detail: {filename}</span>
                </div>
                <RemoveButton onClick={() => onRemove(id)} />
            </div>
        ) : (
            <div className="p-0 flex justify-end relative">
                <SimpleRemoveButton onClick={() => onRemove(id)} />
            </div>
        )}

      <div className="flex-grow overflow-y-auto pl-6 pr-3 pt-0 pb-6 font-sans text-gray-800 select-text">
  {/* Flex container to hold text on the left and image on the right */}
  <div className="mb-2 mt-4 flex justify-between items-start gap-4">
    
    {/* Left side: Title and Description (flex-1 allows it to take up available space) */}
    <div className="flex-1 min-w-0">
      <h2 className="text-[18px] font-bold text-blue-700 break-words">
        {renderValue(titleKey, rowData[titleKey])}
      </h2>
      {descriptionKey && (
        <p className="text-lg text-gray-600 mt-1">
          {renderValue(descriptionKey, rowData[descriptionKey])}
        </p>
      )}
      {sourceBadge && (
                  <div className={`whitespace-nowrap mt-4 rounded-lg text-xs font-semibold ${sourceBadge.className}`}>
                      {sourceBadge.text}
                  </div>
      )}
    </div>

    {/* Right side: Image (flex-shrink-0 prevents the image from being squished) */}
    <div className="flex-shrink-0">
      <TaxonImage taxId={rowData['HOST_TAXID']} taxName={rowData['HOST_TAX_NAME']} expert={viewMode === 'expert'} />
    </div>

  </div>
      <div className="flex flex-row gap-0 mb-4">
      <HostTaxonomyTable data={rowData} />
      <VirusTaxonomyTable data={rowData} />
      </div>

        <div className="space-y-6">
          <section>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 border-b pb-1">Metadata</h3>
            <div className="flex flex-col gap-y-1.5">

              {/* Custom Host line */}  
              { (rowData['HOST_TAX_NAME'] || rowData['HOST_TAXID']) && (
                <div>
                  <div className="flex items-start gap-3">
                      <AutoScalingKey text="Host" />
                      <div className="text-sm text-gray-600 break-words leading-snug flex-grow flex items-center gap-2">
                      {rowData['HOST_TAX_NAME'] && <span>{rowData['HOST_TAX_NAME']}</span>}
                      {rowData['HOST_TAXID'] && renderValue('host_taxid', rowData['HOST_TAXID'])}
                      </div>
                  </div>              
                </div>
                
                )}

              {/* Custom Virus line */}  
              { (rowData['VIRUS_TAX_NAME'] || rowData['VIRUS_TAXID']) && (
                <div>
                  <div className="flex items-start gap-3">
                      <AutoScalingKey text="Virus" />
                      <div className="text-sm text-gray-600 break-words leading-snug flex-grow flex items-center gap-2">
                      {rowData['VIRUS_TAX_NAME'] && <span>{rowData['VIRUS_TAX_NAME']}</span>}
                      {rowData['VIRUS_TAXID'] && renderValue('virus_taxid', rowData['VIRUS_TAXID'])}
                      </div>
                  </div>              
                </div>
                
                )}

            {/* Custom Environment line */}
            {(rowData['ENVIRONMENT_TAX_NAME'] || rowData['ENVIRONMENT_TAXID']) && (
            <div className="flex items-start gap-3">
                <AutoScalingKey text="Environment" />
                <div className="text-sm text-gray-600 break-words leading-snug flex-grow flex items-center gap-2">
                <VariableIcon type="ENVIRONMENT_TAX_NAME" value={rowData['ENVIRONMENT_TAX_NAME']} />
                {rowData['ENVIRONMENT_TAX_NAME'] && <span>{rowData['ENVIRONMENT_TAX_NAME']}</span>}
                {rowData['ENVIRONMENT_TAXID'] && renderValue('environment_taxid', rowData['ENVIRONMENT_TAXID'])}
                </div>
            </div>
            )}
              {/* Custom Location line */}
              {locationDisplay && (
                <div className="flex items-start gap-3">
                  <AutoScalingKey text="Location" />
                  <div className="text-sm text-gray-600 break-words leading-snug flex-grow">{locationDisplay}</div>
                </div>
              )}
              {/* Custom Collection dates line */}
              {collectionDatesDisplay && (
                <div className="flex items-start gap-3">
                  <AutoScalingKey text="Collection dates" />
                  <div className="text-sm text-gray-600 break-words leading-snug flex-grow">{collectionDatesDisplay}</div>
                </div>
              )}

              {entries.map(([key, val]) => {
                if (typeof val === 'object' || val === null || key === "_geojson_str" || key === "geom_id" || val === '' || val === 'No Data' || keysToSkip.includes(key)) return null;
                
                // Split value by pipe to handle multi-value fields
                const valParts = String(val).split('|');

                return (
                  <div key={key} className="flex items-start gap-3">
                    {/* Replace underscores with spaces in label, then capitalize first letter and set rest to lowercase */}
                    <AutoScalingKey text={key.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} />
                    
                    {/* flex-wrap allows multiple values to flow naturally */}
                    <div className="text-sm text-gray-600 break-words leading-snug flex-grow flex flex-wrap items-center gap-y-1">
                      {valParts.map((part, index) => {
                        const trimmedPart = part.trim();
                        return (
                          <React.Fragment key={index}>
                            <span className="inline-flex items-center">
                              {/* Show individual icon for this specific part */}
                              <VariableIcon type={key} value={trimmedPart} />
                              {renderValue(key, trimmedPart)}
                            </span>
                            {/* Visual separator between multi-value items */}
                            {index < valParts.length - 1 && (
                              <span className="text-gray-300 mx-1.5 font-light">|</span>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        {lat && lon && (
        <div className="relative w-full h-80 mt-8 bg-slate-100 rounded-xl overflow-hidden border shadow-inner">
            {MainMapData && (
                <div className="w-full h-full">
                <MapViewer
                    key={`main-map-${id}`}
                    id={`main-map-${id}`}
                    baseLayer="landscape"
                    fallbackLayers={mapFallbacks}
                    viewState={localViewState}
                    disableTooltip={true}
                    onViewStateChange={setLocalViewState}
                    processedData={MainMapData.processedData}
                    geojsonFeatures={bufferGeoJSON ? [bufferGeoJSON] : undefined}
                    stableColorConfig={MainMapData.colorConfig}
                    colorBy="status"
                    radiusMinPixels={6}
                    radiusMaxPixels={6}
                    lineWidth={1}
                    stats={NON_NUMERIC_STATS}
                    onSampleClick={NOOP}   
                />
                </div>
            )}
            {/* Minimap (Overlay) */}
            {MiniMapData && (
            <div className="absolute bottom-2 right-2 w-40 h-36 z-10 
                            bg-[#ebede0] border border-gray-300 rounded-lg 
                            shadow-lg overflow-hidden pointer-events-none">
                {/* Wrap the map in a div that is slightly larger (110%) 
                    to hide the 'canvas edges' */}
                <div className="w-[110%] h-[110%] -translate-x-[5%] -translate-y-[5%]">
                <MapViewer
                    id={`minimap-${id}`}
                    viewState={MiniMapData.viewState}
                    onViewStateChange={() => {}} 
                    processedData={MiniMapData.processedData}
                    baseLayer="landscape"
                    stableColorConfig={MiniMapData.colorConfig}
                    colorBy="status"
                    radiusMinPixels={2}
                    radiusMaxPixels={2}
                    lineWidth={0}
                    stats={NON_NUMERIC_STATS}
                    onSampleClick={NOOP}
                    overlayRasterUrl={gbifRasterUrl}
                    controller={false}               
                />
                </div>
            </div>
            )}
        </div>
        )}
        {(isRunMultiple || hasExtraData) && (
          <section className="mt-8">
            <h3 className="text-xs font-bold text-blue-500 uppercase tracking-widest mb-3 border-b border-blue-100 pb-1 flex justify-between items-center">
              Run details
              
            </h3>

            <div className="relative w-full bg-white mt-6 p-4 rounded-xl border border-gray-100 shadow-sm">
              
              {/* Dropdown Selector */}
              {(isRunMultiple || (extraData && Object.keys(extraData).length > 1)) && (
                <div className="absolute -top-3 right-2 z-10">
                  <select 
                    className="text-xs font-semibold tracking-wide border-2 border-gray-300 rounded-xl px-3 py-1 text-gray-600 bg-white shadow-sm focus:outline-none focus:border-blue-400 cursor-pointer hover:bg-gray-50 transition-colors"
                    value={selectedRun || ''}
                    onChange={(e) => setSelectedRun(e.target.value)}
                  >
                    {runInfo.runs.map(run => (
                      <option key={run} value={run}>{run}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Selected Run Metadata Output */}
              {isRunMultiple && runIndex !== -1 && (
                <div className="flex flex-col gap-y-2 mb-6 mt-2">
                   <div className="flex items-start gap-3">
                     <AutoScalingKey text={runInfo.keys.run ? runInfo.keys.run.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : "Run"} />
                     <div className="text-sm text-gray-600 break-words leading-snug flex-grow flex flex-wrap items-center gap-y-1">
                       <span className="inline-flex items-center">
                         <VariableIcon type={runInfo.keys.run || "RUN"} value={selectedRun} />
                         {renderValue(runInfo.keys.run || 'run', selectedRun)}
                       </span>
                     </div>
                   </div>
                   
                   {runInfo.libraries[runIndex] && (
                     <div className="flex items-start gap-3">
                       <AutoScalingKey text={runInfo.keys.library ? runInfo.keys.library.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : "Librarysource"} />
                       <div className="text-sm text-gray-600 break-words leading-snug flex-grow flex flex-wrap items-center gap-y-1">
                         <span className="inline-flex items-center">
                           <VariableIcon type={runInfo.keys.library || "LIBRARYSOURCE"} value={runInfo.libraries[runIndex]} />
                           {renderValue(runInfo.keys.library || 'librarysource', runInfo.libraries[runIndex])}
                         </span>
                       </div>
                     </div>
                   )}

                   {runInfo.assays[runIndex] && (
                     <div className="flex items-start gap-3">
                       <AutoScalingKey text={runInfo.keys.assay ? runInfo.keys.assay.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : "Assay Type"} />
                       <div className="text-sm text-gray-600 break-words leading-snug flex-grow flex flex-wrap items-center gap-y-1">
                         <span className="inline-flex items-center">
                           <VariableIcon type={runInfo.keys.assay || "ASSAY_TYPE"} value={runInfo.assays[runIndex]} />
                           {renderValue(runInfo.keys.assay || 'assay_type', runInfo.assays[runIndex])}
                         </span>
                       </div>
                     </div>
                   )}

                   {runInfo.platforms[runIndex] && (
                     <div className="flex items-start gap-3">
                       <AutoScalingKey text={runInfo.keys.platform ? runInfo.keys.platform.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : "Platform"} />
                       <div className="text-sm text-gray-600 break-words leading-snug flex-grow flex flex-wrap items-center gap-y-1">
                         <span className="inline-flex items-center">
                           <VariableIcon type={runInfo.keys.platform || "PLATFORM"} value={runInfo.platforms[runIndex]} />
                           {renderValue(runInfo.keys.platform || 'platform', runInfo.platforms[runIndex])}
                         </span>
                       </div>
                     </div>
                   )}
                </div>
              )}


            {/* Radar Chart */}
            {hasExtraData && (
              <h3 className="text-xs font-bold text-blue-400 uppercase tracking-widest border-b border-blue-100 pb-1 flex items-end justify-between mb-12 mt-6">
                <span>Taxonomic assignments</span>
                <div className="flex flex-col items-end gap-0">
                  <span className="text-[9px] lowercase font-normal text-blue-300 italic">
                    
                    <a 
                    href={`https://trace.ncbi.nlm.nih.gov/Traces/?view=run_browser&acc=${selectedRun}&display=analysis`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-[10px] normal-case font-semibold text-blue-500 hover:text-blue-700 hover:underline bg-blue-50 px-1 py-0 rounded transition-colors"
                  >
                    Complete NCBI STAT analysis
                  </a>
                  </span>
                </div>
              </h3>
            )}
              {hasExtraData && (
                <div className="relative w-full h-[350px]">
                  <RadarChart 
                    data={uniqueExtraEntries.map(([key, val]) => ({
                      subject: key.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                      value: Number(val) || 0
                    }))} 
                    totalCount={extraData[selectedRun]?.total_spot_count ? Number(extraData[selectedRun].total_spot_count) : 0} 
                  />
                </div>
              )}

              
            </div>

          
        
          </section>
        )}

        {/* BioProject Details Block */}
        {projectData && (
          <section className="mt-8 mb-6">
            <h3 className="text-xs font-bold text-blue-500 uppercase tracking-widest mb-3 border-b border-blue-100 pb-1 flex justify-between items-center">
              BioProject Details
            </h3>
            <div className="bg-blue-50/40 p-4 rounded-xl border border-blue-100 text-sm text-gray-700 flex flex-col gap-2 shadow-sm">
              <a 
                href={`https://www.ncbi.nlm.nih.gov/bioproject/${projectData.id}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[15px] font-bold text-blue-500 hover:text-blue-600 hover:underline leading-snug"
              >
                {projectData.title || `BioProject: ${projectData.id}`}
              </a>
              
              {projectData.abstract && (
                <div className="mt-3 text-xs leading-relaxed text-gray-600 bg-white p-3 rounded-lg border border-blue-50 max-h-40 overflow-y-auto custom-scrollbar">
                  <span className="font-bold text-gray-700 block mb-1">Abstract</span>
                  {projectData.abstract}
                </div>
              )}
            </div>
          </section>
        )}

        {articleData && (
          <section className="mt-8 mb-6">
            <h3 className="text-xs font-bold text-blue-500 uppercase tracking-widest mb-3 border-b border-blue-100 pb-1 flex justify-between items-center">
              Publication Details
            </h3>
            <div className="bg-blue-50/40 p-4 rounded-xl border border-blue-100 text-sm text-gray-700 flex flex-col gap-2 shadow-sm">
              <a 
                href={`https://pubmed.ncbi.nlm.nih.gov/${articleData.pmid}/`}
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[15px] font-bold text-blue-500 hover:text-blue-600 hover:underline leading-snug"
              >
                {articleData.title}
              </a>
              
              {articleData.authors && (
                <p className="text-xs font-medium text-gray-600 italic leading-relaxed">
                  {articleData.authors}
                </p>
              )}
              
              <div className="text-[13px] font-semibold text-blue-500 flex flex-wrap gap-2 items-center mt-1">
                <span>{articleData.journal} {articleData.year && `(${articleData.year})`}</span>
                {articleData.doi && (
                  <>
                    <span className="text-blue-300">|</span>
                    <a href={`https://doi.org/${articleData.doi}`} target="_blank" rel="noopener noreferrer" className="hover:underline font-medium text-blue-500">
                      doi: {articleData.doi}
                    </a>
                  </>
                )}
              </div>
              
              {articleData.abstract && (
                <div className="mt-3 text-xs leading-relaxed text-gray-600 bg-white p-3 rounded-lg border border-blue-50 max-h-40 overflow-y-auto custom-scrollbar">
                  <span className="font-bold text-gray-700 block mb-1">Abstract</span>
                  {articleData.abstract}
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
});

export default SamplePanel;